import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Paperclip, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { getPresignedUrl, uploadFileToS3, updatePayoutInvoice } from "@/_actions/researchActions";

export default function FileUploadButton({
  payoutId,
  fileType,
  children,
  onUploadSuccess,
  onUploadError,
  acceptedFileTypes = "",
  ...buttonProps
}) {
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type if specified
    if (fileType === "pdf" && file.type !== "application/pdf") {
      toast.error("Por favor selecciona un archivo PDF");
      return;
    }
    if (fileType === "xml" && file.type !== "application/xml" && file.type !== "text/xml") {
      toast.error("Por favor selecciona un archivo XML");
      return;
    }

    setIsUploading(true);

    try {
      // Get presigned URL
      const { presigned_url, s3_key } = await getPresignedUrl(payoutId, fileType);

      // Upload file to S3
      const uploadResponse = await uploadFileToS3(presigned_url, file);

      if (uploadResponse.ok) {
        // Update payout with invoice information
        const invoiceData = {};
        if (fileType === "pdf") {
          invoiceData.pdf_invoice = s3_key;
        } else if (fileType === "xml") {
          invoiceData.xml_invoice = s3_key;
        }

        await updatePayoutInvoice(payoutId, invoiceData);

        setIsUploaded(true);
        toast.success(`Archivo ${fileType.toUpperCase()} subido exitosamente`);
        onUploadSuccess?.(s3_key, file.name);
      } else {
        throw new Error("Error al subir el archivo");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(`Error al subir el archivo: ${error.message}`);
      onUploadError?.(error);
    } finally {
      setIsUploading(false);
      // Reset file input
      event.target.value = "";
    }
  };

  const getButtonIcon = () => {
    if (isUploading) return <Loader2 className="ml-2 animate-spin" />;
    if (isUploaded) return <CheckCircle className="ml-2 text-green-600" />;
    return <Paperclip className="ml-2" />;
  };

  return (
    <>
      <Button {...buttonProps} onClick={handleButtonClick} disabled={isUploading} type="button">
        {children}
        {getButtonIcon()}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileSelect}
        accept={acceptedFileTypes}
        style={{ display: "none" }}
      />
    </>
  );
}
